import { useState } from 'react';
import axios from 'axios';
import { submitAttendance } from '../services/googleScriptServiceCORS';
import { getSuccessMessage } from '../utils/greetings';
import { perfStart, perfEnd } from '../utils/performance';

const generateRequestId = (type, employeeId) => {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now()}_${employeeId || 'emp'}_${type}_${rand}`;
};

export function useAttendanceSubmit({
  employee,
  apiBaseUrl,
  location,
  image,
  explanation,
  requiresExplanation,
  validationWarnings,
  setValidationWarnings,
  setRequiresExplanation,
  setExplanation,
  setImage,
  setImagePreview,
  setTodayAttendance,
  loadTodayAttendance,
  checkValidation,
  setInlineStatus,
  showToast,
  enqueueOfflineIfNeeded,
}) {
  const [loading, setLoading] = useState(false);

  const buildPayload = (type, requestId) => ({
    employee_id: employee.id,
    employee_code: employee.code,
    employee_name: employee.name,
    request_id: requestId || '',
    type,
    latitude: location?.latitude,
    longitude: location?.longitude,
    accuracy: location?.accuracy || null,
    address: location?.address || '',
    explanation: explanation.trim(),
  });

  const validateBeforeSubmit = (type) => {
    if (!location) {
      const msg = 'Vui lòng lấy vị trí trước khi chấm công.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast(msg, 'warning');
      return false;
    }

    if (!image) {
      const msg = 'Vui lòng chụp/chọn ảnh trước khi chấm công.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast(msg, 'warning');
      return false;
    }

    const { needExplanation } = checkValidation(type);
    if ((needExplanation || requiresExplanation) && !explanation.trim()) {
      const msg = validationWarnings.length
        ? `Vui lòng nhập giải trình vì:\n${validationWarnings.join('\n')}`
        : 'Vui lòng nhập giải trình để tiếp tục.';
      setInlineStatus({ type: 'warning', message: msg });
      showToast('Vui lòng nhập giải trình.', 'warning');
      return false;
    }

    return true;
  };

  const resetAfterSuccess = () => {
    setImage(null);
    setImagePreview(null);
    setExplanation('');
    setRequiresExplanation(false);
    setValidationWarnings([]);
  };

  const submitViaFastApi = async (type, requestId) => {
    const formData = new FormData();
    formData.append('employee_id', employee.id);
    formData.append('latitude', location.latitude);
    formData.append('longitude', location.longitude);
    formData.append('address', location.address || '');
    formData.append('request_id', requestId || '');
    formData.append('image', image);

    await axios.post(`${apiBaseUrl}/api/attendance/${type}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const submitGoogleScript = async (type, requestId) => {
    const result = await submitAttendance({ ...buildPayload(type, requestId), image });

    if (result.ok) {
      if (type === 'checkin') {
        setTodayAttendance({
          checkin_time: result.timestamp || new Date().toISOString(),
          checkout_time: null,
          work_hours: 0,
        });
      } else {
        setTodayAttendance((prev) => {
          if (!prev?.checkin_time) return prev;
          const checkoutTime = result.timestamp || new Date().toISOString();
          const workHours = (new Date(checkoutTime) - new Date(prev.checkin_time)) / (1000 * 60 * 60);
          return {
            ...prev,
            checkout_time: checkoutTime,
            work_hours: workHours > 0 ? workHours : 0,
          };
        });
      }
      return;
    }

    if (result.code === 'REQUIRES_EXPLANATION' || result.requiresExplanation) {
      setRequiresExplanation(true);
      setValidationWarnings([result.error, result.note].filter(Boolean));
      throw new Error(result.error || 'Cham cong can giai trinh.');
    }

    throw new Error(result.error || 'Có lỗi xảy ra');
  };

  const runSubmit = async (type) => {
    const requestId = generateRequestId(type, employee?.id);
    if (!validateBeforeSubmit(type)) return;

    const metric = type === 'checkin' ? 'attendance_checkin_submit' : 'attendance_checkout_submit';
    setLoading(true);
    perfStart(metric);

    try {
      const useGoogleScript = process.env.REACT_APP_GOOGLE_SCRIPT_URL;
      if (useGoogleScript) {
        await submitGoogleScript(type, requestId);
      } else {
        await submitViaFastApi(type, requestId);
        await loadTodayAttendance();
      }

      const msg = getSuccessMessage(type);
      setInlineStatus({ type: 'success', message: msg });
      showToast(msg, 'success');
      resetAfterSuccess();
    } catch (error) {
      const queued = await enqueueOfflineIfNeeded({ payload: buildPayload(type, requestId), image, error });
      if (!queued) {
        const errMsg = error.message || error.response?.data?.detail || 'Có lỗi xảy ra khi chấm công';
        setInlineStatus({ type: 'error', message: errMsg });
        showToast(errMsg, 'error');
      }
    } finally {
      perfEnd(metric);
      setLoading(false);
    }
  };

  return {
    loading,
    handleCheckin: () => runSubmit('checkin'),
    handleCheckout: () => runSubmit('checkout'),
  };
}
